// Lead form component
import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { leadSchema, type LeadInput } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { OwnerSelect } from './OwnerSelect'

interface Props {
  defaultValues?: Partial<LeadInput>
  onSubmit: (data: LeadInput) => void
  onCancel?: () => void
  loading?: boolean
  canWriteField?: (field: string) => boolean
}

export function LeadForm({ defaultValues, onSubmit, onCancel, loading, canWriteField = () => true }: Props) {
  const { t } = useTranslation()
  const [showB2bSection, setShowB2bSection] = useState(false)
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LeadInput>({
    resolver: zodResolver(leadSchema),
    defaultValues,
  })

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
            {t('fields.firstName')}
          </label>
          <input
            {...register('firstName')}
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
            {t('fields.lastName')}
          </label>
          <input
            {...register('lastName')}
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700">
          {t('fields.organisation')} <span className="text-red-500">*</span>
        </label>
        <input
          {...register('company')}
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.company && (
          <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            {t('fields.email')}
          </label>
          <input
            {...register('email')}
            type="email"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            {t('fields.phone')}
          </label>
          <input
            {...register('phone')}
            type="tel"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {canWriteField('status') && (
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              {t('fields.status')}
            </label>
            <select
              {...register('status')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="new">{t('status.new')}</option>
              <option value="contacted">{t('status.contacted')}</option>
              <option value="qualified">{t('status.qualified')}</option>
              <option value="unqualified">{t('status.unqualified')}</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
            )}
          </div>
        )}

        {canWriteField('rating') && (
          <div>
            <label htmlFor="rating" className="block text-sm font-medium text-gray-700">
              {t('fields.rating')}
            </label>
            <select
              {...register('rating')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="">{t('common.select')}</option>
              <option value="hot">{t('status.hot')}</option>
              <option value="warm">{t('status.warm')}</option>
              <option value="cold">{t('status.cold')}</option>
            </select>
            {errors.rating && (
              <p className="mt-1 text-sm text-red-600">{errors.rating.message}</p>
            )}
          </div>
        )}

        {canWriteField('source') && (
          <div>
            <label htmlFor="source" className="block text-sm font-medium text-gray-700">
              {t('fields.source')}
            </label>
            <input
              {...register('source')}
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.source && (
              <p className="mt-1 text-sm text-red-600">{errors.source.message}</p>
            )}
          </div>
        )}
      </div>

      {/* Owner assignment - only visible to admins/managers */}
      <Controller
        name="ownerId"
        control={control}
        render={({ field }) => (
          <OwnerSelect
            value={field.value}
            onChange={field.onChange}
            disabled={loading}
          />
        )}
      />

      {/* B2B Details collapsible section */}
      <div>
        <button
          type="button"
          onClick={() => setShowB2bSection(!showB2bSection)}
          className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
        >
          <span>B2B Details</span>
          <ChevronDownIcon
            className={`h-5 w-5 text-gray-500 transition-transform ${showB2bSection ? 'rotate-180' : ''}`}
          />
        </button>

        {showB2bSection && (
          <div className="mt-4 space-y-4">
            {/* Address row */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  {t('fields.city')}
                </label>
                <input
                  {...register('city')}
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                  {t('fields.postalCode')}
                </label>
                <input
                  {...register('postalCode')}
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                  {t('fields.country')}
                </label>
                <input
                  {...register('country')}
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Street */}
            <div>
              <label htmlFor="street" className="block text-sm font-medium text-gray-700">
                {t('fields.street')}
              </label>
              <input
                {...register('street')}
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>

            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                {t('fields.website')}
              </label>
              <input
                {...register('website')}
                type="url"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                {t('fields.description')}
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>

            {/* Tier + Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="tier" className="block text-sm font-medium text-gray-700">
                  Tier
                </label>
                <select
                  {...register('tier')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  <option value="">—</option>
                  <option value="Tier 1">Tier 1</option>
                  <option value="Tier 2">Tier 2</option>
                </select>
              </div>
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  {t('fields.category')}
                </label>
                <input
                  {...register('category')}
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Sales Play + Contact Quality */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="salesPlay" className="block text-sm font-medium text-gray-700">
                  Sales Play
                </label>
                <select
                  {...register('salesPlay')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  <option value="">—</option>
                  <option value="Greenfield">Greenfield</option>
                  <option value="Portal Upgrade">Portal Upgrade</option>
                  <option value="E-Commerce Int">E-Commerce Int</option>
                  <option value="Rip & Replace">Rip &amp; Replace</option>
                </select>
              </div>
              <div>
                <label htmlFor="contactQuality" className="block text-sm font-medium text-gray-700">
                  Contact Quality
                </label>
                <select
                  {...register('contactQuality')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  <option value="">—</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>

            {/* B2B Score + Managing Director */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="b2bScore" className="block text-sm font-medium text-gray-700">
                  B2B Score
                </label>
                <input
                  {...register('b2bScore', { valueAsNumber: true })}
                  type="number"
                  min={0}
                  max={100}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="managingDirector" className="block text-sm font-medium text-gray-700">
                  Managing Director
                </label>
                <input
                  {...register('managingDirector')}
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
